import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { X, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Renderer en QR-kode med "RESOVIA"-tekst i midten som branding.
 * Bruger errorCorrectionLevel: 'H' (≈30% fejl-tolerance) så center-logo ikke bryder QR-læsning.
 *
 * Bemærk: QRCode.toCanvas sætter canvas.style.width/height til render-størrelsen (600px).
 * Vi nulstiller derefter, så CSS/className kan styre display-størrelsen.
 */
async function renderBrandedQr(canvas, url) {
  await QRCode.toCanvas(canvas, url, {
    width: 600,
    margin: 2,
    errorCorrectionLevel: 'H',
    color: { dark: '#000000', light: '#FFFFFF' },
  });
  const ctx = canvas.getContext('2d');
  const size = canvas.width;
  const logoW = size * 0.32;
  const logoH = size * 0.13;
  const x = (size - logoW) / 2;
  const y = (size - logoH) / 2;
  // Hvid baggrundsboks (med lille padding) for at holde QR-koden læsbar
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(x - 6, y - 6, logoW + 12, logoH + 12);
  // RESOVIA-tekst — primær lys-grøn
  ctx.fillStyle = '#65a30d';
  ctx.font = `900 ${Math.round(logoH * 0.62)}px "Helvetica Neue", Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.letterSpacing = '2px';
  ctx.fillText('RESOVIA', size / 2, size / 2);
  // Nulstil display-størrelse så CSS overtager (qrcode-lib sætter 600px inline)
  canvas.style.width = '';
  canvas.style.height = '';
}

export default function GuestQrPrintModal({ guest, onClose, backendUrl, eventName }) {
  const canvasRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState("");

  const isDirect = !!guest?.direct_login_enabled;
  const directToken = guest?.direct_login_token || "";
  const code = guest?.guest_code || "";
  const email = guest?.email || "";
  const validFrom = guest?.valid_from ? new Date(guest.valid_from) : null;
  const validUntil = (guest?.valid_until || guest?.code_expires_at) ? new Date(guest.valid_until || guest.code_expires_at) : null;
  const durationHours = guest?.duration_hours || 24;
  const layers = guest?.visible_layers || {};

  // Liste over hvilke lag gæsten kan se (til print som overblik for admin)
  const LAYER_NAMES = {
    resource_bins: 'Ressourcespande',
    resource_islands: 'Ressourceøer',
    maxicontainers: 'Maxicontainere',
    work_zones: 'Arbejdsområder',
    zones: 'Zoner / områder',
    gps_trackers: 'GPS-trackere',
    food_drinks: 'Boder & barer',
    attention_points: 'Må placere opmærksomhedspunkter',
    analytics_module: 'Analytics-modul',
  };
  const enabledLayers = Object.entries(LAYER_NAMES)
    .filter(([k]) => !!layers[k])
    .map(([, v]) => v);
  // Service-punkter er altid med
  const serviceList = 'Vand, pausetelt, snack, kaffe & te';

  const origin = backendUrl || (typeof window !== "undefined" ? window.location.origin : "");
  const qrUrl = isDirect
    ? `${origin}/?guest_token=${encodeURIComponent(directToken)}`
    : `${origin}/?guest_email=${encodeURIComponent(email)}`;

  useEffect(() => {
    if (!guest || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        await renderBrandedQr(canvasRef.current, qrUrl);
        if (!cancelled) setQrDataUrl(canvasRef.current.toDataURL('image/png'));
      } catch {
        if (!cancelled) setQrDataUrl("");
      }
    })();
    return () => { cancelled = true; };
  }, [guest, qrUrl]);

  const handlePrint = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const fmt = (d) => d ? d.toLocaleString('da-DK', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
    w.document.write(`<!doctype html><html><head><title>Gæsteadgang — ${guest.name || 'Gæst'}</title>
<style>
  @page { size: A4; margin: 1cm; }
  body { font-family: Helvetica, Arial, sans-serif; color: #111; margin: 0; padding: 1cm; }
  .card { width: 15cm; height: 15cm; border: 2px solid #111; border-radius: 8px; padding: 0.6cm; box-sizing: border-box;
          display: flex; flex-direction: column; align-items: center; justify-content: space-between; }
  .header { text-align: center; }
  .header h1 { font-size: 26px; margin: 0 0 2px; letter-spacing: 1px; }
  .header h2 { font-size: 14px; margin: 0 0 2px; font-weight: 500; color: #555; }
  .header .event { font-size: 16px; font-weight: bold; margin-top: 2px; color: #65a30d; text-transform: uppercase; letter-spacing: 1px; }
  .qr { display: flex; align-items: center; justify-content: center; flex: 1; padding: 4px 0; }
  .qr img { width: 8cm; height: 8cm; }
  .info { font-size: 11px; line-height: 1.4; text-align: center; }
  .info b { display: inline-block; min-width: 80px; }
  .code { font-family: 'Courier New', monospace; font-size: 20px; font-weight: bold; letter-spacing: 4px; background: #f4f4f4; padding: 3px 10px; border-radius: 4px; }
  .access { font-size: 10px; line-height: 1.35; text-align: center; color: #555; margin-top: 4px; }
  .access b { color: #111; }
  .footer { font-size: 9px; color: #888; text-align: center; margin-top: 2px; }
</style></head><body>
<div class="card">
  <div class="header">
    <h1>${(guest.name || 'GÆST').toUpperCase()}</h1>
    <h2>${isDirect ? 'Scan QR-koden for direkte adgang til' : 'Scan QR-koden — log ind med email + kode'}</h2>
    ${eventName ? `<div class="event">${eventName}</div>` : ''}
  </div>
  <div class="qr">${qrDataUrl ? `<img src="${qrDataUrl}" />` : ''}</div>
  <div class="info">
    ${!isDirect ? `<div><b>Email:</b> ${email}</div><div><b>Kode:</b> <span class="code">${code}</span></div>` : ''}
    <div><b>Gyldig fra:</b> ${fmt(validFrom)}</div>
    <div><b>Gyldig i:</b> ${durationHours > 0 ? `${durationHours} timer` : 'ubegrænset'}</div>
    ${validUntil ? `<div><b>Udløber:</b> ${fmt(validUntil)}</div>` : ''}
  </div>
  <div class="access">
    <b>Adgang til:</b> ${[serviceList, ...enabledLayers].join(' · ')}
  </div>
  <div class="footer">Resovia · Gæsteadgang</div>
</div>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  if (!guest) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-lg w-full max-w-md shadow-xl flex flex-col max-h-[95vh] sm:max-h-[90vh]" onClick={e => e.stopPropagation()} data-testid="guest-qr-print-modal">
        <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <h3 className="font-bold text-lg truncate pr-2">QR-kode til {guest.name || 'gæst'}</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0 flex-shrink-0"><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-4 space-y-3 text-center overflow-y-auto flex-1 min-h-0">
          <canvas
            ref={canvasRef}
            width={600}
            height={600}
            className="mx-auto block w-48 h-48 sm:w-56 sm:h-56 bg-white p-2 rounded"
            data-testid="guest-qr-image"
          />
          <div className="text-sm space-y-1">
            <p className="font-bold">{isDirect ? '🔓 Direkte adgang via QR' : '🔐 Sikker login'}</p>
            {eventName && <p className="text-xs text-primary uppercase font-bold tracking-wider">{eventName}</p>}
            {!isDirect && (
              <>
                <p className="text-xs text-muted-foreground">Email: <span className="font-mono">{email}</span></p>
                <p className="text-xs text-muted-foreground">Kode: <span className="font-mono font-bold tracking-widest">{code}</span></p>
              </>
            )}
            <p className="text-xs text-muted-foreground">Gyldig fra: {validFrom ? validFrom.toLocaleString('da-DK') : '—'}</p>
            {validUntil && (
              <p className="text-xs text-muted-foreground">Udløber: {validUntil.toLocaleString('da-DK')}</p>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground border-t border-border pt-2 mt-2">
            <p><span className="font-bold text-foreground">Adgang til:</span> {[serviceList, ...enabledLayers].join(' · ')}</p>
          </div>
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={onClose}>Luk</Button>
          <Button size="sm" onClick={handlePrint} disabled={!qrDataUrl} data-testid="print-guest-qr-btn">
            <Printer className="h-3 w-3 mr-1" /> Print QR-kort (15×15 cm)
          </Button>
        </div>
      </div>
    </div>
  );
}
